// ImportFrameworkDialog — modal for importing an obligation framework.
// Two tabs: "Platform Library" (select from pre-loaded entries) and "Upload File" (CSV upload).
// Both tabs share: name field, category picker (defaults to currently selected category).
// On success: closes, invalidates queries, navigates to the new framework.
// On parse error (422): displays errors in-dialog, does not close.

import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { useSelectedObligationCategory } from './ObligationLayout'
import {
  obligationsApi,
  type LibraryEntry,
  type ImportResult,
  type ImportError as ImportErrorType,
} from '@/lib/api/obligations'
import { categoriesApi } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { X, Upload, Library, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

// --- Props ---

interface ImportFrameworkDialogProps {
  open: boolean
  onClose: () => void
}

// --- Tabs ---

type ImportTab = 'library' | 'upload'

// --- Component ---

export function ImportFrameworkDialog({ open, onClose }: ImportFrameworkDialogProps) {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const api = useApi()
  const oblApi = obligationsApi(api)
  const catApi = categoriesApi(api, 'framework')
  const { categoryId } = useSelectedObligationCategory()

  // Form state
  const [tab, setTab] = useState<ImportTab>('library')
  const [name, setName] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState('')
  const [selectedLibraryId, setSelectedLibraryId] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [parseErrors, setParseErrors] = useState<ImportErrorType[]>([])

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTab('library')
      setName('')
      setSelectedCategoryId(categoryId ?? '')
      setSelectedLibraryId('')
      setFile(null)
      setError(null)
      setParseErrors([])
    }
  }, [open, categoryId])

  // Fetch library entries
  const { data: libraryEntries, isLoading: libraryLoading } = useQuery({
    queryKey: queryKeys.complianceFrameworks.library(slug),
    queryFn: () => oblApi.listLibrary(),
    enabled: open,
  })

  // Fetch categories for picker
  const { data: categories } = useQuery({
    queryKey: queryKeys.categories.list(slug, 'framework'),
    queryFn: () => catApi.list(),
    enabled: open,
  })

  function handleLibrarySelect(entry: LibraryEntry) {
    setSelectedLibraryId(entry.id)
    // Pre-populate name if empty or still matches a previous library entry name
    if (!name || libraryEntries?.some((e) => e.name === name)) {
      setName(entry.name)
    }
    setParseErrors([])
    setError(null)
  }

  // Import mutation — library
  const libraryMutation = useMutation({
    mutationFn: () =>
      oblApi.importFromLibrary({
        library_id: selectedLibraryId,
        name: name.trim(),
        category_id: selectedCategoryId,
      }),
    onSuccess: handleSuccess,
    onError: handleError,
  })

  // Import mutation — CSV upload
  const csvMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file selected')
      return oblApi.importFromCSV(file, name.trim(), selectedCategoryId)
    },
    onSuccess: handleSuccess,
    onError: handleError,
  })

  function handleSuccess(result: ImportResult) {
    // Invalidate framework lists so they pick up the new entry
    queryClient.invalidateQueries({ queryKey: queryKeys.complianceFrameworks.all(slug) })
    onClose()
    // Navigate to the new framework
    navigate({ to: `/t/${slug}/obligations/${result.framework_id}` })
  }

  function handleError(err: Error) {
    if (err instanceof ApiRequestError) {
      // 422 = parse errors — the response body has the error list
      if (err.status === 422 && err.body) {
        try {
          const body = typeof err.body === 'string' ? JSON.parse(err.body) : err.body
          if (body.errors && Array.isArray(body.errors)) {
            setParseErrors(body.errors as ImportErrorType[])
            setError(`${body.error_count} format error${body.error_count !== 1 ? 's' : ''} found. No data was imported.`)
            return
          }
        } catch {
          // Fall through to generic error
        }
      }
      setError(err.message)
    } else {
      setError(err.message)
    }
  }

  function handleSubmit() {
    setError(null)
    setParseErrors([])

    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (!selectedCategoryId) {
      setError('Category is required')
      return
    }

    if (tab === 'library') {
      if (!selectedLibraryId) {
        setError('Select a framework from the library')
        return
      }
      libraryMutation.mutate()
    } else {
      if (!file) {
        setError('Select a CSV file to upload')
        return
      }
      csvMutation.mutate()
    }
  }

  const isPending = libraryMutation.isPending || csvMutation.isPending

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 flex w-full max-w-lg flex-col rounded-lg border bg-background shadow-lg"
        style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Add Obligation Framework</h2>
          <button className="rounded-sm p-1 hover:bg-accent" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6">
          <button
            className={cn(
              'flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              tab === 'library'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
            onClick={() => { setTab('library'); setParseErrors([]); setError(null) }}
          >
            <Library className="h-4 w-4" />
            Platform Library
          </button>
          <button
            className={cn(
              'flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              tab === 'upload'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
            onClick={() => { setTab('upload'); setParseErrors([]); setError(null) }}
          >
            <Upload className="h-4 w-4" />
            Upload File
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {/* Error display */}
            {error && (
              <div className="flex items-start gap-2 rounded bg-destructive/10 p-3 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p>{error}</p>
                  {parseErrors.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs">
                      {parseErrors.map((pe, i) => (
                        <li key={i}>
                          Row {pe.row_number}: {pe.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* Tab content: Library */}
            {tab === 'library' && (
              <div>
                <Label>Select Framework</Label>
                <div className="mt-1 max-h-48 overflow-y-auto rounded-md border">
                  {libraryLoading ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      Loading library...
                    </div>
                  ) : !libraryEntries || libraryEntries.length === 0 ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      No frameworks available in the platform library.
                    </div>
                  ) : (
                    libraryEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className={cn(
                          'cursor-pointer px-3 py-2 text-sm transition-colors hover:bg-accent/50',
                          selectedLibraryId === entry.id && 'bg-primary/10 font-medium text-primary',
                        )}
                        onClick={() => handleLibrarySelect(entry)}
                      >
                        <div className="flex items-center justify-between">
                          <span>{entry.name}</span>
                          <span className="text-xs text-muted-foreground">{entry.version_label}</span>
                        </div>
                        {entry.description && (
                          <p className="mt-0.5 text-xs text-muted-foreground">{entry.description}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Tab content: Upload */}
            {tab === 'upload' && (
              <div>
                <Label htmlFor="csv-file">CSV File</Label>
                <Input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  className="mt-1"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null)
                    setParseErrors([])
                    setError(null)
                  }}
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  CSV format: requirement_group, sub_requirement, reference_code, title, description.
                  First row must be a header.
                </p>
              </div>
            )}

            {/* Common fields: Name */}
            <div>
              <Label htmlFor="framework-name">Name *</Label>
              <Input
                id="framework-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. ISO 27001:2022"
                className="mt-1"
              />
            </div>

            {/* Common fields: Category */}
            <div>
              <Label htmlFor="framework-category">Category *</Label>
              <select
                id="framework-category"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <option value="">Select a category</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {'—'.repeat(cat.depth)} {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </div>
    </div>
  )
}
